/**
 * An audio note's playback: play and pause, where it is, how long it is, seeking and speed.
 * Shared by `AudioNotePlayer` and the desktop project record's audio note.
 */
import { useEffect, useRef, useState } from 'react';

export const PLAYBACK_RATES = [1, 1.5, 2] as const;

export const useAudioNotePlayback = (src: string) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rateIndex, setRateIndex] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      if (isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnded = () => {
      setIsPlaying(false);
      audio.currentTime = 0;
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch((error) => {
        console.error('Audio playback failed:', error);
        setIsPlaying(false);
      });
  };

  const setRate = (nextIndex: number) => {
    const audio = audioRef.current;
    setRateIndex(nextIndex);
    if (audio) audio.playbackRate = PLAYBACK_RATES[nextIndex];
  };

  const cycleRate = () => setRate((rateIndex + 1) % PLAYBACK_RATES.length);

  /** Moves to `position` seconds. */
  const seekTo = (position: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = position;
    setCurrentTime(position);
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;
  const remaining = duration ? Math.max(duration - currentTime, 0) : 0;

  return {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    rateIndex,
    togglePlay,
    setRate,
    cycleRate,
    seekTo,
    progress,
    remaining,
  };
};
